<template>
    <div id="comments">
        <div id="app-layout-right-column__content__title" :class="{invisible: !bin.id}">
            <span>{{ !isGuest && currentChannel == 'shared' ? 'Shared Discussion Channel' : 'Discussion' }}</span>
        </div>

        <div id="comments-container" v-show="image && account.plan_id > 1"
             :class="{'d-grid': image && comments.length == 0 && account.plan_id > 1}"
        >
            <component :is="commentTemplate" v-for="comment in comments" :key="comment.id" :comment="comment" />

            <span v-if="image && comments.length == 0" class="select-asset-message text-caps">
                There are no comments.<br />
                Be the first one to post a comment.
            </span>
        </div>


        <div class="comments-editor-container"
             v-show="image && account.plan_id > 1"
             :class="{'--shared-channel': !isGuest && currentChannel == 'shared'}"
        >
            <span v-if="whoIsTyping" class="is-typing-message">{{ typingUser.name }} is typing...</span>

            <img class="avatar" :src="getGravatar(user.email)" alt="" />

            <vue-editor :customModules="customModulesForEditor" :editorOptions="editorSettings" ref="commentsEditor"
                        @text-change="onTextChange"
                        v-model="content"
            />

            <MentionedAssets />

            <div id="comments-toolbar" class="comments-tools-container">
                <button class="ql-bold"><span class="material-icons">format_bold</span></button>
                <button class="ql-italic"><span class="material-icons">format_italic</span></button>

                <span class="separator"></span>

                <span @click="openMentionDialog('@')" class="material-icons" v-tippy content="Mention User">alternate_email</span>
                <span @click="openMentionDialog('#')" class="material-icons"
                      v-tippy content="Mention Asset"
                >tag</span>

                <span v-if="canUploadFiles" class="separator"></span>

                <i v-if="canUploadFiles && currentChannel == 'media'"
                   class="material-icons import-images icon-btn"
                   :class="{ disabled: !canUploadFiles }"
                   @click="canUploadFiles ? openImportDialog() : null"
                   v-tippy
                   content="Upload Files"
                >
                    backup
                </i>
                <input type="file" @change="startImportProcess" id="import-comment-media" class="d-none" multiple />

                <span @click="createMessage" class="material-icons color-primary" :class="{ disabled: content == '' || uploadStatus || uploadingAsset }">send</span>
            </div>
        </div>

        <span v-show="!image && account.plan_id > 1" class="select-asset-message text-caps">
            Please select an asset to view discussion thread
        </span>

        <PlanUpgradeNotificationInSidebar v-if="account.plan_id == 1" />
    </div>

</template>

<script>
import CommentTemplate from '@/components/app/comments/CommentTemplate';
import MentionedAssets from '@/components/app/comments/MentionedAssets';

import { VueEditor } from "vue2-editor";
import Mention from "quill-mention";
import { mapGetters, mapState } from "vuex";
import AvatarMixins from "@/mixins/Avatar";
import FilenameMixin from "@/mixins/Filename";
import CanMixin from "@/mixins/CanMixin";
import PlanUpgradeNotificationInSidebar from "@/components/app/ui/PlanUpgradeNotificationInSidebar.vue";

export default {
    name: "Comments",
    mixins: [AvatarMixins, FilenameMixin, CanMixin],
    components: {
        PlanUpgradeNotificationInSidebar,
        VueEditor,
        MentionedAssets
    },

    mounted() {
        this.$root.commentsEditor = this.$refs.commentsEditor;

        // on key ENTER
        this.$root.commentsEditor.quill.keyboard.bindings[13].unshift({
            key: 13,
            handler: async (range, context) => {
                await this.createMessage();
            }
        });

        document.onpaste = async (evt) => {
            let dT = evt.clipboardData || window.clipboardData;
            if (dT.files.length == 0) return;

            await this.setImportDestination();
            this.$store.dispatch("Comments/uploadingAssetLoader", true);
            window.loader.uploadImages(true, dT.files);
        };

        // this.$root.commentsEditor.quill.root.addEventListener('focus', this.onFocus);
    },

    data() {
        return {
            content: '',
            waitForTimeout: false,

            customModulesForEditor: [{ alias: "mention", module: Mention }],
            editorSettings: {
                formats: ["bold", "italic", "mention"],
                placeholder: 'Write a comment...',
                modules: {
                    toolbar: '#comments-toolbar',
                    mention: {
                        allowedChars: /^[A-Za-z0-9\sÅÄÖåäö]*$/,
                        mentionDenotationChars: ["@", "#", "$", "%"],
                        dataAttributes: ['id', 'value', 'denotationChar', 'fileType', 'commentUpload'],
                        fixMentionsToQuill: true,
                        defaultMenuOrientation: 'top',
                        offsetLeft: -38,
                        source: (searchTerm, renderList, mentionChar) => {
                            let values = [];

                            if (mentionChar === "@") values = this.atValues;
                            if (mentionChar === "#" && this.currentChannel == 'media') values = this.hashValues();
                            if (mentionChar === "$" && this.currentChannel == 'media') values = this.hashValues('candidate');
                            if (mentionChar === "%" && this.currentChannel == 'media') values = this.hashValues('match');

                            if (mentionChar === "#" && this.currentChannel == 'shared') {
                                values = this.hashValues('candidate');
                                values = values.filter(item => this.$store.getters['Projects/fileHasTag'](item.file, 'shared', 'shared'));
                            }

                            if (searchTerm.length === 0) {
                                renderList(values, searchTerm);
                            } else {
                                const matches = [];
                                for (let i = 0; i < values.length; i++)
                                    if (
                                        ~values[i].value.toLowerCase().indexOf(searchTerm.toLowerCase())
                                    )
                                        matches.push(values[i]);
                                renderList(matches, searchTerm);
                            }
                        },
                        renderItem: (item, searchTerm) => {
                            let name = item.value;
                            if (item.id == this.account.id) name += " (You)";

                            if (item.type == 'account') {
                                let account = this.$store.getters['Organizations/memberAccountByUserId'](item.user_id);
                                if (account.pivot.suspended_at) name += '<span class="color-warning"> (suspended)</span>';
                                return `<div class="item-container"><img class="avatar" src="${item.img}" /><span class="name">${name}</span></div>`;
                            } else {
                                // TODO code taken from Thumbnail compoennt
                                let thumbnail = '';

                                if (!this.fileThumbnailObjects[item.id]) {
                                    this.$store.dispatch('Projects/fetchThumbnail', {
                                        file: item.file,
                                        project_id: this.project_id
                                    }).then(thumbnailData => {
                                        thumbnail = URL.createObjectURL(thumbnailData.blob);

                                        document.querySelector(`.comments-editor-container .ql-mention-list-item[data-id="${item.id}"] .placeholder-container`).remove();
                                        let imgElement = document.createElement('img');
                                        imgElement.setAttribute('src', thumbnail);
                                        imgElement.setAttribute('class', 'thumbnail');
                                        document.querySelector(`.comments-editor-container .ql-mention-list-item[data-id="${item.id}"] .item-container`).prepend(imgElement);

                                        let thumbnailObject = {
                                            file_id: item.id,
                                            objectURL: thumbnail
                                        };

                                        this.$store.dispatch('Projects/setThumbnailObject', thumbnailObject);
                                    })
                                } else {
                                    thumbnail = this.fileThumbnailObjects[item.id];
                                }

                                let html = '<div class="item-container">';
                                if (thumbnail) html += `<img class="thumbnail" src="${thumbnail}" />`;
                                else html += window.UI.createFileThumbnailPlaceholder();
                                html += `<span class="name">${name}</span>`;

                                let isCommentUpload = this.$store.getters['Projects/fileHasTag'](item.file, 'comment_upload', 1);
                                let fileType = this.capitalizeFirstLetter(item.file.type);

                                html += `<span class="type">${isCommentUpload ? 'Comment upload' : fileType}</span>`;
                                html += '</div>';

                                return html;
                            }
                        }
                    }
                }
            },
        }
    },

    computed: {
        ...mapState('Comments', ['comments', 'replyTo', 'whoIsTyping', 'currentChannel', 'uploadingAsset']),
        ...mapGetters('Comments', ['atValues', 'hashValues', 'assetMentionsFromDelta']),
        ...mapGetters('User', ['isGuest']),
        ...mapGetters('Projects', ['folderIdBySlug']),
        ...mapState("User", ["user", "account"]),
        ...mapState("Projects", ["fileThumbnailObjects", "project_id", "image", "bin", "currentFile", "uploadStatus"]),
        commentTemplate() {
            return CommentTemplate;
        },
        typingUser() {
            return this.$store.getters['Projects/userOfActiveProject'](this.whoIsTyping.user_id);
        },
    },

    methods: {
        async createMessage() {
            if (this.content == '' || this.uploadStatus || this.uploadingAsset) return;

            let delta = this.$refs.commentsEditor.quill.getContents();
            this.content = '';

            let data = {
                mentions: [],
            };

            delta.ops.forEach(item => {
                if (item?.insert?.mention) {
                    // getting rid of unnecessary long strings
                    delete item.insert.mention.file;
                    delete item.insert.mention.img;
                    delete item.insert.mention.thumbnail;

                    let mention = item.insert.mention;
                    data.mentions.push({
                        type: mention.denotationChar == "@" ? 'account' : 'file',
                        fileType: mention.fileType,
                        id: mention.id,
                        text: mention.value,
                    })
                }
            })

            data.text = JSON.stringify(delta);

            if (this.replyTo) data.message_id = this.replyTo.id;

            this.$refs.commentsEditor.quill.setContents([]);
            this.$store.dispatch('Comments/replyTo', null);

            data.mentions.forEach(mention => {
                if (mention.fileType !== 'candidate') return;
                if (this.$store.state.User.account.role_id > 5) return;
                if (this.$store.state.Comments.currentChannel !== 'shared') return;

                let candidate = this.$store.getters["Projects/candidateById"](mention.id);
                if (this.$store.getters["Projects/fileHasTag"](candidate, 'shared', 'shared')) return;

                this.$root.projectTree.shareFile(candidate);
            })

            await this.$store.dispatch('Comments/create', data);
            $('#comments-container').scrollTop($('#comments-container')[0].scrollHeight + 12);
        },

        openMentionDialog(key) {
            let mentionModule = this.$refs.commentsEditor.quill.getModule('mention');
            if (mentionModule.isOpen) {
                mentionModule.hideMentionList();
            } else {
                mentionModule.openMenu(key);
            }
        },

        onTextChange(delta, oldDelta, source) {
            if (this.$refs.commentsEditor.removingMentionedAsset) return;

            let currentDelta = this.$refs.commentsEditor.quill.getContents();
            this.$store.dispatch("Comments/setMentionedAssets", this.assetMentionsFromDelta(currentDelta));

            if (!this.waitForTimeout) {
                this.waitForTimeout = true;

                let payload = {
                    action: 'typing',
                    user_id: this.$store.state.User.user.id,
                    bin_id: this.$store.state.Projects.bin.id,
                    channel: this.$store.state.Comments.currentChannel
                };

                if (this.$store.state.Comments.currentChannel == 'media') {
                    this.$root.projectChannel.presence.update(payload);
                }

                if (this.$store.state.Comments.currentChannel == 'shared') {
                    this.$root.projectChannelShared.presence.update(payload);
                }

                setTimeout(() => {
                    this.waitForTimeout = false;
                }, 4000)
            }

            if (oldDelta.ops[0].insert?.mention?.denotationChar === '@' && delta.ops[0].delete) this.$store.dispatch('Comments/replyTo', null);
        },

        insertMention(denotationChar, mention, toBeginning = false) {
            let mentionModule = this.$refs.commentsEditor.quill.getModule('mention');
            if (toBeginning) this.$refs.commentsEditor.quill.setSelection(0, 0, 'api');

            mentionModule.insertItem(
                {
                    denotationChar,
                    ...mention,
                },
                true
            );

            this.removeNewLines()
            this.positionCursorAtEnd();
        },

        removeNewLines() {
            let delta = this.$refs.commentsEditor.quill.getContents();
            if (delta.ops[0].insert == "\n") {
                delta.ops.shift();

                if (delta.ops[1].insert == "\n \n") {
                    delta.ops[1].insert = " \n";
                }

                this.$refs.commentsEditor.quill.setContents(delta);
            }
        },

        positionCursorAtEnd() {
            // setTimeout(() => {
                this.$refs.commentsEditor.quill.setSelection(this.$refs.commentsEditor.quill.getLength());
            // }, 0);
        },

        onFocus() {
            // let delta = this.$refs.commentsEditor.quill.getContents();
            // if (delta.ops.length > 1 || (delta.ops.length == 1 && delta.ops[0].insert !== "\n")) return;
            // let mentionModule = this.$refs.commentsEditor.quill.getModule('mention');

            // let denotationChar = null;
            // let values = [];
            // let mention = {};

            // if (this.$store.state.Projects.candidate) {
            //     denotationChar = "$";
            //     values = this.hashValues('candidate');
            //     mention = values.find(value => value.id == this.$store.state.Projects.candidate);
            // } else {
            //     denotationChar = "#";
            //     values = this.hashValues();
            //     mention = values.find(value => value.id == this.$store.state.Projects.image);
            // }
            //
            // mentionModule.insertItem(
            //     {
            //         denotationChar,
            //         ...mention
            //     },
            //     true
            // );
            //
            // if (this.$store.state.Projects.match.id) {
            //     denotationChar = "%";
            //     values = this.hashValues('match');
            //     mention = values.find(value => value.id == this.$store.state.Projects.match.id);
            //     mentionModule.insertItem(
            //         {
            //             denotationChar,
            //             ...mention
            //         },
            //         true
            //     );
            // }
        },

        async setImportDestination() {
            let mediaFolderId = this.folderIdBySlug('media');

            await this.$store.dispatch("Projects/setAction", {
                type: "image",
                action: "comment-upload",
                id: mediaFolderId,
            });
        },

        async openImportDialog() {
            await this.setImportDestination();
            document.getElementById("import-comment-media").click();
        },

        startImportProcess() {
            this.$store.dispatch("Comments/uploadingAssetLoader", true);
            window.loader.uploadImages(true);
        },
    }
}
</script>

<style lang="scss" scoped>
.select-asset-message,
.upgrade-plan-message {
    align-self: center;
    line-height: 1.5;
    text-align: center;
}

#comments {
    height: 100%;
    display: grid;
    grid-template-rows: auto 1fr auto;
    //padding: 12px 8px 20px;
}

#comments-container {
    overflow-y: scroll;
    padding-bottom: 35px;
    display: flex;
    flex-direction: column;
}

.comments-editor-container {
    position: relative;
    display: grid;
    grid-template-columns: 30px calc(100% - 38px);
    grid-row-gap: 8px;
    grid-column-gap: 8px;
    border: initial;
    border: 2px solid var(--primary);
    padding-top: 8px;
    border-radius: 8px;
    width: 100%;

    &.--shared-channel {
        border-color: var(--color-danger);
    }

    .is-typing-message {
        position: absolute;
        top: -25px;
    }

    .avatar {
        margin-right: 8px;
        margin-left: 8px;
    }

    ::v-deep .ql-mention-denotation-char {
        display: none;
    }
}

#comments-toolbar {
    grid-column: 1 / 3;
    border: none;
    border-top: 0.5px solid var(--main-bg-color);
    //height: 30px;
    padding: 8px;
    display: flex;
    align-items: center;
    //width: 100%;

    &:after {
        display: none;
    }

    ::v-deep button {
        &.ql-active {
            .ql-stroke {
                stroke: var(--primary);
            }
        }

        .ql-stroke {
            stroke: var(--font-default-color);
        }
    }

    > span {
        font-size: 18px;
        cursor: pointer;

        &:not(:last-of-type) {
            margin-right: 10px;
        }

        &:last-of-type {
            margin-left: auto;
        }

        &.separator {
            background: var(--main-bg-color);
            height: 100%;
            width: 0.5px;
            display: block;
            margin: 0 14px 0 10px;
        }
    }

    .icon-btn {
        cursor: pointer;
        font-size: 18px;
    }
}

::v-deep .quillWrapper {
    #quill-container {
        border: none;
    }

    .ql-editor {
        min-height: initial;
        padding: 8px 0 0 8px;

        * {
            font-family: Poppins;
            line-height: 1.25;
            font-size: 12px;
        }


        .mention {
            color: var(--primary);
            &[data-comment-upload="true"] {
                display: none;
            }
        }

        //&[data-denotation-char="#"] {
        //    cursor: pointer;
        //}
    }

    .ql-editor.ql-blank::before {
        color: var(--font-default-color) !important;
        font-size: 12px;
        font-family: Poppins;
        font-style: initial;
        top: 8px;
        line-height: 1.25;
    }

    .ql-mention-list-container {
        padding: 25px 0;
        background-color: var(--dark-gray);
        box-shadow: 2px 2px 10px 0px #0e0e0e;
        border: 1px solid rgba(0, 0, 0, 0.15);
        border-radius: var(--box-rounding);
        transition: none;
    }

    .ql-mention-list {
        padding-left: initial;
        max-height: 280px;
        overflow-y: scroll;
        scrollbar-color: #989898 transparent;
        scrollbar-width: thin;

        &::-webkit-scrollbar {
            width: 5px;
        }

        &::-webkit-scrollbar-thumb {
            background-color: #989898;
            border-radius: 50px;
        }
    }

    .ql-mention-list-item {
        line-height: 20px;
        padding: 2px 25px;
        font-size: 1rem;
        font-family: Poppins;
        cursor: pointer;

        &:not(:last-of-type) {
            margin-bottom: 2px;
        }

        &:hover {
            transition: all 0.05s ease-in;
            background: #363636;
        }

        .item-container {
            display: flex;
            align-items: center;

            .name {
                //width: 150px;
                text-overflow: ellipsis;
                overflow: hidden;
                white-space: nowrap;
                margin-right: 15px;
                line-height: 1.2;
            }

            .avatar {
                width: 20px;
                height: 20px;
            }

            .thumbnail {
                width: 35px;
                height: 20px;
                object-fit: cover;
            }

            .type {
                margin-left: auto;
                white-space: nowrap;
            }

            img, .placeholder-container {
                margin-right: 4px;
            }

            .placeholder-preview {
                width: 40px;
                height: 25px;
            }
        }
    }
}


</style>